import { useEffect } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { Skeleton, Stack } from "@mui/material";
import DoorFrontIcon from "@mui/icons-material/DoorFront";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import FlashlightOffIcon from "@mui/icons-material/FlashlightOff";
import FlashlightOnIcon from "@mui/icons-material/FlashlightOn";
import SecurityIcon from "@mui/icons-material/Security";

import { enqueueSnackbar } from "notistack";

import { useGetAvailablePortsQuery } from "services/streamingApi/siteCommands.api";

import { PortType } from "types/PortType";
import { PortTypeSection } from "./PortTypeSection";
import { useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import { appFeaturesSelectors } from "features/appFeatures/store/selectors";
import { MatchType } from "features/appFeatures/types/matchType";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";

export interface PortTypeSectionListProps {
    siteId: EntityId;
}

export function PortTypeSectionList(props: PortTypeSectionListProps) {
    // Check if the current user has the permissions to allow port control
    const enablePortControlFeature = useAppSelector((state: RootState) =>
        appFeaturesSelectors.selectUserHasFeatures(
            state,
            [
                AppFeature.SiteOperationsDoors,
                AppFeature.SiteOperationsEmergency,
                AppFeature.SiteOperationsClubLights,
                AppFeature.SiteOperationsAssist,
                AppFeature.SiteOperationsFireReset,
            ],
            MatchType.Any,
        ),
    );

    const {
        data: availablePorts,
        isLoading: isLoadingAvailablePorts,
        isError: isErrorAvailablePorts,
    } = useGetAvailablePortsQuery(props.siteId, {
        refetchOnMountOrArgChange: true,
        skip: !enablePortControlFeature,
    });

    useEffect(() => {
        if (isErrorAvailablePorts) {
            enqueueSnackbar(`Failed getting available ports of club`, {
                variant: "error",
            });
        }
    }, [isErrorAvailablePorts]);

    return (
        enablePortControlFeature && (
            <>
                {isLoadingAvailablePorts && (
                    <Stack spacing={1}>
                        <Skeleton
                            variant="rectangular"
                            width={120}
                            height={30}
                        />
                        <Skeleton
                            variant="rectangular"
                            height={40}
                        />
                        <Skeleton
                            variant="rectangular"
                            height={40}
                        />
                        <Skeleton
                            variant="rectangular"
                            width={120}
                            height={30}
                        />
                        <Skeleton
                            variant="rectangular"
                            height={40}
                        />
                        <Skeleton
                            variant="rectangular"
                            height={40}
                        />
                    </Stack>
                )}
                {isErrorAvailablePorts && <span>Unable to load ports</span>}

                {!isLoadingAvailablePorts && availablePorts && (
                    <Stack spacing={1}>
                        <FeatureFlagGuard
                            accessibleWithFeatureFlags={[
                                AppFeature.SiteOperationsDoors,
                            ]}
                        >
                            <PortTypeSection
                                key={`${props.siteId}-${PortType.Door}-section`}
                                sectionName="Doors"
                                ports={availablePorts.filter(
                                    (p) => p.portType === PortType.Door,
                                )}
                                siteId={props.siteId}
                                activePortIcon={<MeetingRoomIcon />}
                                inactivePortIcon={<DoorFrontIcon />}
                            ></PortTypeSection>
                        </FeatureFlagGuard>

                        <FeatureFlagGuard
                            accessibleWithFeatureFlags={[
                                AppFeature.SiteOperationsClubLights,
                            ]}
                        >
                            <PortTypeSection
                                key={`${props.siteId}-${PortType.ClubLights}-section`}
                                sectionName="Lights"
                                ports={availablePorts.filter(
                                    (p) => p.portType === PortType.ClubLights,
                                )}
                                siteId={props.siteId}
                                activePortIcon={<FlashlightOnIcon />}
                                inactivePortIcon={<FlashlightOffIcon />}
                            ></PortTypeSection>
                        </FeatureFlagGuard>

                        <FeatureFlagGuard
                            accessibleWithFeatureFlags={[
                                AppFeature.SiteOperationsEmergency,
                                AppFeature.SiteOperationsFireReset,
                            ]}
                            matchType={MatchType.Any}
                        >
                            <PortTypeSection
                                key={`${props.siteId}-${PortType.Emergency}-section`}
                                sectionName="Emergency"
                                ports={availablePorts.filter(
                                    (p) =>
                                        p.portType === PortType.Emergency ||
                                        p.portType === PortType.FireReset,
                                )}
                                siteId={props.siteId}
                                activePortIcon={<NotificationsActiveIcon />}
                                inactivePortIcon={<NotificationsOffIcon />}
                            ></PortTypeSection>
                        </FeatureFlagGuard>

                        <FeatureFlagGuard
                            accessibleWithFeatureFlags={[
                                AppFeature.SiteOperationsAssist,
                            ]}
                        >
                            <PortTypeSection
                                key={`${props.siteId}-${PortType.Assist}-section`}
                                sectionName="Assistance"
                                ports={availablePorts.filter(
                                    (p) => p.portType === PortType.Assist,
                                )}
                                siteId={props.siteId}
                                activePortIcon={<SecurityIcon />}
                                inactivePortIcon={<SecurityIcon />}
                            ></PortTypeSection>
                        </FeatureFlagGuard>
                    </Stack>
                )}
            </>
        )
    );
}
